import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Check, Eye, EyeOff, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';

import { BrandSignature } from '@/presentation/components/index.js';
import type { AuthController } from '@/presentation/controllers/auth-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useAuth } from '@/presentation/hooks/use-auth.js';
import { ROUTES } from '@/shared/constants/routes.js';

interface Props {
    controller: AuthController;
    mode?: 'reset' | 'first-access';
}

type TokenState = 'validating' | 'valid' | 'invalid';

export function ResetPasswordPage({ controller, mode = 'reset' }: Props) {
    const { translate } = useApp();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') ?? '';
    const isFirstAccess = mode === 'first-access';
    const { resetPassword, firstAccess, validateResetToken, isLoading, error } = useAuth(controller);

    const [tokenState, setTokenState] = useState<TokenState>('validating');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (!token) {
            setTokenState('invalid');
            return;
        }
        (async () => {
            const valid = await validateResetToken(token);
            setTokenState(valid ? 'valid' : 'invalid');
        })();
    }, [token, validateResetToken]);

    const rules = [
        { label: translate('auth.passwordMin8'), test: (v: string) => v.length >= 8 },
        { label: translate('auth.passwordUpper'), test: (v: string) => /[A-Z]/.test(v) },
        { label: translate('auth.passwordLower'), test: (v: string) => /[a-z]/.test(v) },
        { label: translate('auth.passwordNumber'), test: (v: string) => /[0-9]/.test(v) }
    ];
    const allPassed = rules.every((r) => r.test(newPassword));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!allPassed) {
            return;
        }
        if (newPassword !== confirmPassword) {
            return;
        }
        const success = isFirstAccess ? await firstAccess(token, newPassword) : await resetPassword(token, newPassword);
        if (success) {
            setSuccess(true);
            setTimeout(() => navigate(ROUTES.LOGIN), 3000);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                background: 'linear-gradient(135deg, #12122B 0%, #1a1340 50%, #12122B 100%)'
            }}
        >
            <Box sx={{ width: '100%', maxWidth: 400 }}>
                <Box sx={{ mb: 4 }}>
                    <BrandSignature variant="centered" logoHeight={40} />
                </Box>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                    <CardContent sx={{ p: 4 }}>
                        {tokenState === 'validating' && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <CircularProgress size={32} />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                    {translate('auth.validatingLink')}
                                </Typography>
                            </Box>
                        )}
                        {tokenState === 'invalid' && (
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                                <X size={28} color="var(--mui-palette-error-main)" />
                                <Typography variant="h6" fontWeight={700} sx={{ mt: 1, mb: 1 }}>
                                    {translate('auth.invalidLink')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    {translate('auth.invalidLinkMsg')}
                                </Typography>
                                <Button variant="contained" sx={{ color: 'white' }} component={RouterLink} to={ROUTES.FORGOT_PASSWORD}>
                                    {translate('auth.requestNewLink')}
                                </Button>
                            </Box>
                        )}
                        {tokenState === 'valid' && !success && (
                            <>
                                <Typography variant="h5" fontWeight={700} gutterBottom>
                                    {isFirstAccess ? translate('auth.setInitialPassword') : translate('auth.redefinePassword')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    {isFirstAccess ? translate('auth.setInitialPasswordSubtitle') : translate('auth.redefinePasswordSubtitle')}
                                </Typography>
                                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ position: 'relative' }}>
                                        <TextField
                                            label={translate('auth.newPassword')}
                                            type={showNew ? 'text' : 'password'}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            size="small"
                                            fullWidth
                                        />
                                        <IconButton onClick={() => setShowNew(!showNew)} sx={{ position: 'absolute', right: 4, top: 4 }} tabIndex={-1} size="small">
                                            {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </IconButton>
                                    </Box>
                                    {newPassword.length > 0 && (
                                        <List dense>
                                            {rules.map((rule) => (
                                                <ListItem key={rule.label} sx={{ py: 0 }}>
                                                    {rule.test(newPassword) ? (
                                                        <Check size={13} color="var(--mui-palette-success-main)" />
                                                    ) : (
                                                        <X size={13} color="var(--mui-palette-text-disabled)" />
                                                    )}
                                                    <Typography variant="caption" sx={{ ml: 1, color: rule.test(newPassword) ? 'success.main' : 'text.disabled' }}>
                                                        {rule.label}
                                                    </Typography>
                                                </ListItem>
                                            ))}
                                        </List>
                                    )}
                                    <Box sx={{ position: 'relative' }}>
                                        <TextField
                                            label={translate('auth.confirmNewPassword')}
                                            type={showConfirm ? 'text' : 'password'}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            size="small"
                                            fullWidth
                                            error={!!confirmPassword && confirmPassword !== newPassword}
                                        />
                                        <IconButton onClick={() => setShowConfirm(!showConfirm)} sx={{ position: 'absolute', right: 4, top: 4 }} tabIndex={-1} size="small">
                                            {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </IconButton>
                                    </Box>
                                    {error && (
                                        <Typography color="error" variant="body2">
                                            {error}
                                        </Typography>
                                    )}
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        sx={{ color: 'white' }}
                                        disabled={isLoading || !allPassed || newPassword !== confirmPassword}
                                    >
                                        {isLoading ? (
                                            <CircularProgress size={20} color="inherit" />
                                        ) : isFirstAccess ? (
                                            translate('auth.setPassword')
                                        ) : (
                                            translate('auth.redefinePassword')
                                        )}
                                    </Button>
                                </Box>
                            </>
                        )}
                        {success && (
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                                <Check size={28} color="var(--mui-palette-success-main)" />
                                <Typography variant="h6" fontWeight={700} sx={{ mt: 1, mb: 1 }}>
                                    {translate('auth.passwordRedefined')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {translate('auth.passwordRedirecting')}
                                </Typography>
                            </Box>
                        )}
                        {tokenState !== 'invalid' && !success && (
                            <Box sx={{ textAlign: 'center', mt: 3 }}>
                                <Link component={RouterLink} to={ROUTES.LOGIN} variant="body2">
                                    {translate('auth.backToLogin')}
                                </Link>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}
